import { useOptionalLabel } from '@/lib/i18n';

/**
 * labels.ts — how a result's BAND becomes words, in one place.
 *
 * A band arrives as a plain string on `display.dimensions[].band` and in a
 * headline's `vars`, and it could come from either closed set the contract
 * defines — `Band` (Low/Average/High) or `SkillBand`
 * (Emerging/Proficient/Advanced) — or from neither, because the whole point of
 * the `display` projection is that a consumer can draw a model it has never
 * seen. Three call sites need the same answer (the charts, the narrative list,
 * the headline sentence) and were growing three implementations of it; the third
 * one is what moved it here.
 *
 * Nothing in this file names a model. It asks the catalogue, in a fixed order,
 * and reports what it found.
 */

/**
 * A band's label, or `null` if neither closed set claims the value.
 *
 * `useOptionalLabel`, not `useLabel`, and that is the load-bearing choice: a
 * caller that must DECIDE on the answer cannot tell "translated" from "echoed
 * back" by comparing strings, because `band.High` translates to the string
 * `High` in English. Only the lookup itself knows.
 *
 * **Case-tolerant**, because the two spellings are both in the wild: `08` writes
 * interpretation keys lower-case (`conscientiousness.high`) while the contract's
 * enums are capitalised (`High`, `Advanced`). Trying both is cheaper than
 * assuming one and silently dropping every key written the other way.
 */
export function useOptionalBandLabel(): (band: string | undefined | null) => string | null {
  const optional = useOptionalLabel();
  return (band) => {
    if (!band) return null;
    for (const spelling of [band, band.charAt(0).toUpperCase() + band.slice(1)]) {
      const found = optional('band', spelling) ?? optional('skillBand', spelling);
      if (found) return found;
    }
    return null;
  };
}

/**
 * A band's label, falling back to the value itself — what a badge needs.
 *
 * A fourth plugin's own band name is not a string we own, and printing it
 * verbatim is more honest than printing a key or an empty badge.
 */
export function useBandLabel(): (band: string) => string {
  const optional = useOptionalBandLabel();
  return (band) => optional(band) ?? band;
}
